import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";

import { useTheme } from "../../useTheme";
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";
import "./SchemeBar.css";

/**
 * Slim top bar for the scheme pages (station schemes and low-tension boards).
 * The full app header is too tall here: these pages scale the sheet into the
 * free height, so every pixel of chrome comes out of the drawing.
 *
 * It also marks <body> with `has-scheme-bar`, which is what the page CSS keys
 * off to push its own top-anchored controls below the bar.
 */
const SchemeBar = ({
    title, subtitle, data, onBack, hideBack, children,
} : {
    title?: string,
    subtitle?: string,
    data?: any,
    onBack?: () => void,
    /** Pages with their own back control (the HT modules have a floating one)
     *  pass this, so the bar does not show a second identical arrow. */
    hideBack?: boolean,
    /** Optional middle slot — the learning module puts its step strip here. */
    children?: React.ReactNode,
}) => {

    const navigate = useNavigate();
    const [cookies] = useCookies();
    const en = cookies?.language === "en";
    const [theme, toggleTheme] = useTheme();

    useEffect(() => {
        document.body.classList.add("has-scheme-bar");
        return () => document.body.classList.remove("has-scheme-bar");
    }, []);

    const user = data?.userData;

    return (
        <header className="scheme-bar">
            {!hideBack ? (
                <button
                    type="button"
                    className="sb-back"
                    onClick={() => (onBack ? onBack() : navigate(-1))}
                    title={en ? "Back" : "Înapoi"}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            ) : null}

            <img className="sb-logo" src={ZenLogoWhite} alt="ZenVolt" />

            <div className="sb-titles">
                {title ? <span className="sb-title">{title}</span> : null}
                {subtitle ? <span className="sb-subtitle">{subtitle}</span> : null}
            </div>

            {children ? <div className="sb-slot">{children}</div> : null}

            <div className="sb-right">
                <button
                    type="button"
                    className="sb-icon-btn"
                    onClick={toggleTheme}
                    title={theme === "dark"
                        ? (en ? "Light mode" : "Mod luminos")
                        : (en ? "Dark mode" : "Mod întunecat")}
                >
                    <FontAwesomeIcon icon={theme === "dark" ? faSun : faMoon} />
                </button>

                {user ? (
                    <span className="sb-user" title={user.username}>
                        {user.username}
                    </span>
                ) : null}
            </div>
        </header>
    );
};

export default SchemeBar;
